'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_SIDE_PANEL_WIDTH,
  readSidePanelWidth,
  writeSidePanelWidth,
} from './copSidePanelWidth';

/**
 * How long after the last change the width is written, in ms.
 *
 * The resize handle reports a width per pointer move, so persisting on every
 * change would write to `localStorage` at animation frame rate for the length of
 * a drag. The effect below cancels its pending write on each new width, so what
 * lands is the width the operator let go at, once.
 */
const PERSIST_DELAY_MS = 300;

/**
 * The width of the COP's right column, remembered per operator.
 *
 * It used to be `window.innerWidth * 0.3` clamped to 560 and recomputed on every
 * load, which meant a drag survived a dock switch and nothing else: an operator
 * who pulled the column in to 320 got 560 back the next time they signed in. It
 * is a working preference now, stored the way the manual stage layout and the
 * split-video choice already are.
 *
 * Read on the FIRST render, not in an effect. `(dashboard)/layout.tsx` returns
 * `DashboardInitializing` while auth is loading, so the COP never renders on the
 * server and never renders before the operator is known: there is no hydration
 * to mismatch and no window where the identity is unknown. Reading in an effect
 * instead cost a visible snap on every load - an operator whose stored width is
 * 560 would see the panel, the map's key column and the command bar's centring
 * all jump one frame in.
 *
 * The effect below still re-reads, because the operator CAN change without this
 * hook unmounting, and a shared console must not open on the previous
 * operator's column.
 */
export function useSidePanelWidth({ operator = null }: { operator?: string | null } = {}): {
  width: number;
  setWidth: (next: number) => void;
} {
  const [width, setWidthState] = useState(() =>
    typeof window === 'undefined'
      ? DEFAULT_SIDE_PANEL_WIDTH
      : readSidePanelWidth(operator, window.innerWidth),
  );

  // Set the moment the operator moves the handle: after that this session's
  // width is theirs and a read must not go over it.
  const draggedRef = useRef(false);
  const previousOperatorRef = useRef<string | null>(operator);

  useEffect(() => {
    const previous = previousOperatorRef.current;
    previousOperatorRef.current = operator;

    // A different PERSON, as opposed to an identity merely resolving from null:
    // their stored width wins, and this session's drag was not theirs. Carrying
    // it over would write operator A's preference into operator B's key, which
    // is the leak the per-operator key exists to prevent - and a shared console
    // is a real deployment here.
    if (previous !== null && previous !== operator) draggedRef.current = false;

    if (draggedRef.current) return;
    setWidthState(readSidePanelWidth(operator, window.innerWidth));
  }, [operator]);

  const setWidth = useCallback((next: number) => {
    draggedRef.current = true;
    setWidthState(next);
  }, []);

  // What the pending write would put where, read by the flush below. A ref
  // because the unmount cleanup must see the LAST width, not the one that was
  // current when its effect was set up.
  const pendingRef = useRef<{ width: number; operator: string | null } | null>(null);

  useEffect(() => {
    // Nothing to persist until the operator has actually chosen a width. Writing
    // the default here would pin every operator to whatever it happened to be on
    // the day they first opened the panel.
    if (!draggedRef.current) return;
    pendingRef.current = { width, operator };
    const timer = setTimeout(() => {
      writeSidePanelWidth(width, operator);
      pendingRef.current = null;
    }, PERSIST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [width, operator]);

  // Flush on the way out. Without it, letting go of the handle and navigating
  // away - or logging out - inside the debounce window loses the width silently,
  // which is the one thing a remembered preference must not do.
  useEffect(
    () => () => {
      const pending = pendingRef.current;
      if (pending) writeSidePanelWidth(pending.width, pending.operator);
    },
    [],
  );

  return { width, setWidth };
}
